import { useEffect } from "react";

/**
 * Global text interaction layer.
 * - IntersectionObserver adds `.in-view` to elements with `.interactive-text`
 *   when they scroll into the viewport, triggering soft scale + glow.
 * - Click delegate adds a short `.pop` class + a radial ripple element
 *   (GPU transforms / opacity only — no layout thrash).
 *
 * Targets only elements explicitly marked `.interactive-text` in JSX
 * (headlines, stat values, key phrases, tags) — paragraphs are untouched.
 */
const TextInteractions = () => {
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) {
      // Ensure snap-reveal elements are visible without animation
      document.querySelectorAll(".snap-reveal").forEach((el) => el.classList.add("revealed"));
      return;
    }

    const selector = ".interactive-text";
    const snapSelector = ".snap-reveal";

    // --- Reveal observer for section-level .snap-reveal (play once) ---
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("revealed");
            revealObserver.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    const revealTracked = new WeakSet();
    const scanReveal = () => {
      document.querySelectorAll(snapSelector).forEach((el) => {
        if (!revealTracked.has(el)) {
          revealObserver.observe(el);
          revealTracked.add(el);
        }
      });
    };
    scanReveal();

    // --- IntersectionObserver ---
    let observer;
    const tracked = new WeakSet();

    const attachObserver = () => {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add("in-view");
            } else {
              // Re-trigger the lift each time it re-enters the viewport
              e.target.classList.remove("in-view");
            }
          });
        },
        { threshold: 0.35, rootMargin: "0px 0px -10% 0px" }
      );

      document.querySelectorAll(selector).forEach((el) => {
        if (!tracked.has(el)) {
          observer.observe(el);
          tracked.add(el);
        }
      });
    };

    attachObserver();

    // Re-scan periodically for nodes mounted later (framer-motion reveals etc.)
    const rescan = setInterval(() => {
      if (!observer) return;
      document.querySelectorAll(selector).forEach((el) => {
        if (!tracked.has(el)) {
          observer.observe(el);
          tracked.add(el);
        }
      });
      scanReveal();
    }, 1500);

    const RIPPLE_COLORS = {
      "interactive-text--cyan": "rgba(0,240,255,0.45)",
      "interactive-text--magenta": "rgba(255,0,255,0.45)",
      "interactive-text--yellow": "rgba(255,222,0,0.45)",
      "interactive-text--gold": "rgba(255,184,69,0.45)",
    };
    const DEFAULT_RIPPLE = "rgba(255,255,255,0.35)";

    const getRippleColor = (el) => {
      for (const cls of Object.keys(RIPPLE_COLORS)) {
        if (el.classList.contains(cls)) return RIPPLE_COLORS[cls];
      }
      return DEFAULT_RIPPLE;
    };

    // --- Click pop + ripple (event delegation) ---
    const onClick = (ev) => {
      const el = ev.target.closest(selector);
      if (!el) return;

      // Pop animation
      el.classList.remove("pop");
      // force reflow to restart animation
      void el.offsetWidth;
      el.classList.add("pop");
      setTimeout(() => el.classList.remove("pop"), 400);

      // Ripple
      const rect = el.getBoundingClientRect();
      const x = ev.clientX - rect.left;
      const y = ev.clientY - rect.top;

      const color = getRippleColor(el);

      const ripple = document.createElement("span");
      ripple.className = "uth-ripple";
      const size = Math.max(rect.width, rect.height) * 1.2;
      ripple.style.width = `${size}px`;
      ripple.style.height = `${size}px`;
      ripple.style.left = `${x}px`;
      ripple.style.top = `${y}px`;
      ripple.style.background = `radial-gradient(circle, ${color} 0%, transparent 70%)`;

      // Ensure host can contain the absolute ripple
      const prevPos = getComputedStyle(el).position;
      if (prevPos === "static") {
        el.style.position = "relative";
      }

      el.appendChild(ripple);
      setTimeout(() => ripple.remove(), 700);
    };

    document.addEventListener("click", onClick, { passive: true });

    return () => {
      clearInterval(rescan);
      if (observer) observer.disconnect();
      revealObserver.disconnect();
      document.removeEventListener("click", onClick);
    };
  }, []);

  return null;
};

export default TextInteractions;
